import { formatPropertyUnitLabel } from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import type {
    InboxWorkflowItem,
    InboxWorkflowRowOutcomeMap,
    InboxWorkflowState,
    InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import { formatInboxSourceLabel } from "../../../../../_shared/_logic/cells-presentation"
import { normalizeContactPointsOutput } from "../../../_shared/_logic/contact-point-output"
import {
  contactPointKindLabel,
  isPhoneContactPointKind,
} from "../../../../../../../../../../../_shared/_logic/contact-point-kinds"
import type {
  ContactPointDecision,
  ContactPointKind,
  ContactPointReviewDraft,
  ContactPointReviewRow,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const normalizedKind = (...values: unknown[]): ContactPointKind => {
  const kind = stringValue(...values).toUpperCase()

  if (kind === "EMAIL" || kind === "MOBILE" || kind === "FIXED_LINE" || kind === "ADDRESS")
    return kind

  return ""
}

const kindLabel = (kind: ContactPointKind) => (kind ? contactPointKindLabel(kind) : "Kontaktweg")

const stepOutput = (item: InboxWorkflowItem, stepKey: string): JsonObject =>
  objectValue(item.steps.find((entry) => entry.step_key === stepKey)?.output)

const relevantItem = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) =>
  item.action_case.flow_key === workflow.flow.flow_key &&
  (item.steps.some((entry) => entry.step_key === step.step_key) ||
    step.requirements_resolved_by_step.some((requirement) =>
      item.action_case.requirements.includes(requirement),
    ))

const itemNeedsStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.some(
    (requirement) =>
      item.action_case.requirements.includes(requirement) &&
      !item.action_case.resolved_requirements.includes(requirement),
  )

const itemResolvedStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = item.steps.find((entry) => entry.step_key === step.step_key)

  if (projected?.status === "resolved") return true

  if (projected?.status === "skipped") return false

  const requirements = step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const contactPoint = (value: unknown) => {
  const point = objectValue(value)

  return {
    id: stringValue(point.id, point.contact_point_id) || null,
    kind: normalizedKind(point.kind, point.contact_point_kind),
    value: stringValue(point.masked_display_value, point.display_value, point.value),
  }
}

const propertyPresentation = (payload: JsonObject) => {
  const property = objectValue(payload.property)
  const address = objectValue(property.address)
  const unit = objectValue(property.unit)

  return {
    propertyId: stringValue(property.id, payload.property_id) || null,
    addressLabel:
      stringValue(
        address.label,
        address.freeform,
        property.label,
        payload.address_label,
        payload.property_label,
        payload.property_address,
        payload.address,
      ) || "Liegenschaft",
    unitLabel: formatPropertyUnitLabel(
      stringValue(
        unit.label,
        unit.hint,
        payload.property_unit_label,
        payload.unit_label,
        payload.unit_hint,
      ) || "Gebäudeeinheit",
    ),
  }
}

const customerPresentation = (payload: JsonObject) => {
  const requester = objectValue(payload.requester)
  const customer = objectValue(requester.verified_customer)
  const fallbackCustomer = objectValue(requester.customer)
  const contact = objectValue(requester.contact)
  const customerId = stringValue(customer.id, fallbackCustomer.id, payload.customer_id) || null

  return {
    customerId,
    customerLabel:
      stringValue(
        customer.label,
        customer.name,
        fallbackCustomer.label,
        fallbackCustomer.name,
        requester.submitted_name,
        payload.customer_label,
        payload.customer_name,
      ) || "Ansprechpartner",
    customerRoleLabel:
      stringValue(
        customer.role,
        fallbackCustomer.role,
        customer.role_hint,
        fallbackCustomer.role_hint,
        requester.submitted_role,
        requester.suggested_role,
        payload.customer_role_hint,
        payload.property_customer_role,
      ) || (customerId ? "Ansprechpartner" : ""),
    customerEmail: stringValue(contact.email, payload.contact_email, payload.email),
    customerPhone: stringValue(contact.phone, payload.contact_phone, payload.phone),
  }
}

const sourcePresentation = (payload: JsonObject, item: InboxWorkflowItem) => {
  const presentation = objectValue(payload.presentation)
  const origin = objectValue(payload.origin)

  return {
    sourceLabel: formatInboxSourceLabel(
      stringValue(
        presentation.source_label,
        payload.source_label,
        origin.source,
        item.action_case.source,
        item.action_case.channel,
      ),
    ),
    sourceSubLabel: stringValue(
      presentation.source_subline,
      presentation.source_sub_label,
      payload.source_subline,
    ),
  }
}

const comparisonPoint = (
  comparison: JsonObject,
  side: "existing" | "submitted",
  kind: "email" | "phone",
) => contactPoint(comparison[`${side}_${kind}`])

const durableOutputError = "Gespeicherte Kontaktpunkt-Entscheidung fehlt oder ist ungültig."

export const buildContactPointReviewRows = ({
  workflow,
  step,
  rowOutcomes,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes: InboxWorkflowRowOutcomeMap
}): ContactPointReviewRow[] =>
  workflow.items
    .filter((item) => relevantItem(item, workflow, step))
    .map((item) => {
      const actionCaseId = item.action_case.id
      const payload = objectValue(item.action_case.payload)
      const review = objectValue(payload.review)
      const comparison = objectValue(
        Object.keys(objectValue(review.contact_comparison)).length
          ? review.contact_comparison
          : payload.contact_comparison,
      )
      const target = contactPoint(payload.target_contact_point)
      const replacement = contactPoint(payload.replacement_contact_point)
      const reviewKind =
        stringValue(payload.request_kind).toLowerCase() === "delete_contact_point"
          ? "delete_contact_point"
          : "contact_point_change"
      const deletion = target.id ? target : replacement
      const conflictingFields = Array.isArray(review.conflicting_contact_fields)
        ? review.conflicting_contact_fields
        : Array.isArray(payload.conflicting_contact_fields)
          ? payload.conflicting_contact_fields
          : []
      const hintedKind = normalizedKind(
        target.kind,
        replacement.kind,
        conflictingFields.find((field) => typeof field === "string" && field.trim()),
      )
      const emailExisting = comparisonPoint(comparison, "existing", "email")
      const emailSubmitted = comparisonPoint(comparison, "submitted", "email")
      const phoneExisting = comparisonPoint(comparison, "existing", "phone")
      const phoneSubmitted = comparisonPoint(comparison, "submitted", "phone")
      const comparedPhoneKind = phoneSubmitted.kind || phoneExisting.kind
      const contactPointKind =
        reviewKind === "delete_contact_point"
          ? deletion.kind
          : hintedKind ||
            (emailSubmitted.value || emailExisting.value
              ? "EMAIL"
              : phoneSubmitted.value || phoneExisting.value
                ? comparedPhoneKind
                : "")
      const submitted =
        contactPointKind === "EMAIL"
          ? emailSubmitted
          : isPhoneContactPointKind(contactPointKind)
            ? phoneSubmitted
            : replacement.id || replacement.value
              ? replacement
              : target
      const existing =
        contactPointKind === "EMAIL"
          ? emailExisting
          : isPhoneContactPointKind(contactPointKind)
            ? phoneExisting
            : target
      const property = propertyPresentation(payload)
      const customer = customerPresentation(payload)
      const source = sourcePresentation(payload, item)
      const output = stepOutput(item, "review_contact_points")
      const needsStep = itemNeedsStep(item, step)
      const resolved = itemResolvedStep(item, step)
      const skipped =
        item.steps.find((entry) => entry.step_key === step.step_key)?.status === "skipped"
      const outcome = rowOutcomes[actionCaseId] ?? null
      const completed = resolved || outcome?.status === "success"
      const normalizedOutput = normalizeContactPointsOutput(output)
      const persistedMutation =
        normalizedOutput?.decisions.length === 1 ? normalizedOutput.decisions[0] : null
      const decision: ContactPointDecision | null = persistedMutation?.action ?? null
      const outputError = resolved && !persistedMutation ? durableOutputError : null
      const emptyPoint = { id: null, kind: "" as ContactPointKind, value: "" }
      const historicalAffected = persistedMutation?.affectedContactPoint
        ? {
            id: persistedMutation.affectedContactPoint.contactPointId,
            kind: normalizedKind(persistedMutation.affectedContactPoint.kind),
            value: persistedMutation.affectedContactPoint.value,
          }
        : emptyPoint
      const historicalReplacement = persistedMutation?.replacementContactPoint
        ? {
            id: persistedMutation.replacementContactPoint.contactPointId,
            kind: normalizedKind(persistedMutation.replacementContactPoint.kind),
            value: persistedMutation.replacementContactPoint.value,
          }
        : emptyPoint
      const rowReviewKind = resolved
        ? persistedMutation?.action === "delete"
          ? "delete_contact_point"
          : "contact_point_change"
        : reviewKind
      const rowDeletion = resolved ? historicalAffected : deletion
      const rowContactPointKind = resolved ? historicalAffected.kind : contactPointKind
      const rowSubmitted = resolved
        ? persistedMutation?.action === "replace"
          ? historicalReplacement
          : historicalAffected
        : submitted
      const rowExisting = resolved
        ? persistedMutation?.action === "replace" || persistedMutation?.action === "delete"
          ? historicalAffected
          : emptyPoint
        : existing

      return {
        id: actionCaseId,
        actionCaseId,
        workflowItem: item,
        step: item.steps.find((entry) => entry.step_key === step.step_key) ?? null,
        payload: item.action_case.payload,
        ...source,
        ...property,
        ...customer,
        reviewKind: rowReviewKind,
        deletionContactPointId: rowDeletion.id,
        deletionContactKind: rowDeletion.kind,
        deletionValueLabel: rowDeletion.value,
        contactPointKind: rowContactPointKind,
        contactPointKindLabel: kindLabel(rowContactPointKind),
        submittedContactValue: rowSubmitted.value,
        existingContactValue: rowExisting.value,
        submittedContactPointId: rowSubmitted.id,
        existingContactPointId: rowExisting.id,
        persistedDecision: decision,
        needsStep,
        rowOutcome: outcome,
        isOpen: needsStep,
        isResolved: resolved,
        isCompatible: item.compatible,
        disabled: completed || !item.compatible || Boolean(outputError),
        statusLabel:
          outcome?.status === "failed"
            ? "Prüfung nötig"
            : outputError
              ? "Datenfehler"
              : skipped
                ? "Übersprungen"
                : completed
                  ? "Erledigt"
                  : item.compatible
                    ? "Offen"
                    : "Nicht verfügbar",
        statusDetail: outcome?.error || outcome?.detail || outputError || item.error || null,
        searchText: [
          source.sourceLabel,
          property.addressLabel,
          property.unitLabel,
          customer.customerLabel,
          customer.customerRoleLabel,
          customer.customerEmail,
          customer.customerPhone,
          rowSubmitted.value,
          rowExisting.value,
        ].join(" "),
      }
    })

export const defaultContactPointReviewDraft = (
  row: ContactPointReviewRow,
): ContactPointReviewDraft => ({
  value: row.submittedContactValue,
  action:
    row.persistedDecision ??
    (!row.needsStep
      ? ""
      : row.reviewKind === "delete_contact_point"
        ? "delete"
        : !row.submittedContactValue
          ? "reject"
          : row.existingContactValue
            ? "replace"
            : "append"),
})
